import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { toast } from 'react-toastify';
import { 
  XMarkIcon,
  UserPlusIcon,
  EnvelopeIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const AddShopAccountModal = ({ isOpen, onClose, onSuccess, shop }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: shop?.email || '',
    password: ''
  });

  const resetForm = () => {
    setFormData({
      email: shop?.email || '',
      password: ''
    });
    setShowPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({ ...prev, password }));
    toast.success(t('randomPasswordGenerated'));
  };

  const validateForm = () => {
    if (!formData.email || !formData.email.trim()) {
      toast.error('Please enter an email address');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!shop?.id) {
      toast.error('Shop ID is required');
      return;
    }

    setLoading(true);
    
    try {
      const client = getApiClient();
      await client.post('/admin/shop/add-account', {
        shopId: shop.id,
        email: formData.email,
        password: formData.password
      });

      toast.success(t('firebaseAccountCreatedSuccessfully'));
      resetForm();
      onSuccess(); // Refresh the shop details
      onClose();
      
    } catch (error) {
      console.error('Error creating Firebase account:', error);
      const errorMessage = error.response?.data?.message || error.message || t('failedToCreateFirebaseAccount');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-3/5 lg:w-1/2 max-w-xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <UserPlusIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">
              {t('addShopAccount')}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Shop Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Shop Information</h4>
          <div className="text-sm text-blue-700">
            <p><strong>Brand Name:</strong> {shop?.name}</p>
            <p><strong>Current Email:</strong> {shop?.email}</p>
            {shop?.taxIdentifier && <p><strong>Tax ID:</strong> {shop?.taxIdentifier}</p>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('email')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="admin@autoservice.ro"
                disabled={loading}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t('thisWillBeTheLoginEmail')}
            </p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('password')} *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="block w-full pl-10 pr-20 py-3 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter secure password"
                disabled={loading}
                minLength={8}
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pr-3 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <div className="mt-2 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {t('minimumCharactersRequired')}
              </p>
              <button
                type="button"
                onClick={generateRandomPassword}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
                disabled={loading}
              >
                {t('generateRandom')}
              </button>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <UserPlusIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t('firebaseAccountCreation')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This will create a Firebase user account for the shop with the following details:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Shop ID: {shop?.id}</li>
                    <li>Authentication email: {formData.email}</li>
                    <li>Account will be linked to this auto shop</li>
                    <li>Shop will be able to log in with these credentials</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              disabled={loading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('creatingAccount')}
                </div>
              ) : (
                t('createFirebaseAccount')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddShopAccountModal;